// ReleaseValidations.tsx (v4) — STEP-EPD-VALIDATION-VISIBILITY
// Operator view of ephemeral-staging validation records.
// Satisfies PRIN-SIGNAL-INTEGRITY: operator can decide promote/hold
// with drill-down via workflow_run_url.
import { useCallback, useEffect, useState } from "react";
import type { ReleaseValidationsResponse, StagingValidationEntry } from "../../api/types.js";
import { api } from "../../api/client.js";

function GateChip({ state }: { state: StagingValidationEntry["promotion_gate_state"] }) {
  const color =
    state === "passed"
      ? "var(--ok)"
      : state === "blocked"
        ? "var(--danger)"
        : "var(--text-dim)";
  const label = state === "passed" ? "GATE PASSED" : state === "blocked" ? "GATE BLOCKED" : "UNKNOWN";
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.6,
        color,
        border: `1px solid ${color}`,
        borderRadius: 3,
        padding: "1px 5px",
        fontFamily: "var(--font-mono)",
      }}
    >
      {label}
    </span>
  );
}

function ResultBadge({ result }: { result: string }) {
  const ok = result === "pass";
  return (
    <span
      style={{
        color: ok ? "var(--ok)" : result === "fail" ? "var(--danger)" : "var(--text-dim)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
      }}
    >
      {result}
    </span>
  );
}

function ValidationRow({ v }: { v: StagingValidationEntry }) {
  const date = (() => {
    try {
      const d = new Date(v.validated_at);
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")} UTC`;
    } catch {
      return v.validated_at;
    }
  })();

  return (
    <div
      style={{
        padding: "12px 0",
        borderBottom: "1px solid var(--border-soft)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 600, color: "var(--text)" }}>
          {v.release_ref}
        </span>
        <GateChip state={v.promotion_gate_state} />
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
          smoke: <ResultBadge result={v.smoke_result} />
        </span>
        <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
          integration: <ResultBadge result={v.integration_result} />
        </span>
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 4, flexWrap: "wrap" }}>
        {v.duration_ms != null && (
          <span style={{ fontSize: 10, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
            {v.duration_ms}ms
          </span>
        )}
        {v.cost_usd != null && (
          <span style={{ fontSize: 10, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
            ${v.cost_usd.toFixed(4)}
          </span>
        )}
        <span style={{ fontSize: 10, color: "var(--text-dim)" }}>{date}</span>
      </div>
      {v.workflow_run_url && (
        <a
          href={v.workflow_run_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 10,
            color: "var(--accent)",
            fontFamily: "var(--font-mono)",
            textDecoration: "none",
          }}
        >
          ↗ View run
        </a>
      )}
    </div>
  );
}

export function ReleaseValidationsScreen() {
  const [data, setData] = useState<ReleaseValidationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.releaseValidations();
      setData(result);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div style={{ padding: "0 16px 80px" }}>
      <div
        style={{
          fontSize: 9,
          textTransform: "uppercase",
          letterSpacing: 0.7,
          color: "var(--text-dim)",
          margin: "16px 0 12px",
          fontWeight: 600,
        }}
      >
        Staging Validations
      </div>
      {loading && (
        <div style={{ color: "var(--text-dim)", fontSize: 12, textAlign: "center", padding: 24 }}>
          Loading…
        </div>
      )}
      {error && (
        <div style={{ color: "var(--danger)", fontSize: 12, padding: "12px 0" }}>
          {error}
        </div>
      )}
      {!loading && !error && data && (
        <>
          {data.validations.length === 0 ? (
            <div style={{ color: "var(--text-dim)", fontSize: 12, padding: "12px 0" }}>
              No validation records found.
            </div>
          ) : (
            data.validations.map((v) => (
              <ValidationRow key={`${v.release_ref}-${v.validated_at}`} v={v} />
            ))
          )}
          <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 12 }}>
            Generated {new Date(data.generatedAt).toUTCString()}
          </div>
        </>
      )}
    </div>
  );
}
